import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { RequestState } from '@kfolks/types'
import { Badge } from '@/shared/components/atoms/ui/badge'
import { Button } from '@/shared/components/atoms/ui/button'
import { Card } from '@/shared/components/atoms/ui/card'
import { Input } from '@/shared/components/atoms/ui/input'
import { type ListRequestParams, type RequestDTO, requestCenterApi } from '../api/request-center.api'
import { RequestStateBadge } from '../components/RequestStateBadge'
import { ActionNeededRowDecor, ReplyRequiredChip } from './_infoRequestNotice'
import { requestCenterKeys } from '../hooks/query-keys'

/**
 * State-filter dropdown options. Sourced from the `RequestState` enum so a
 * future state addition surfaces here automatically. Labels match
 * `<RequestStateBadge>` for visual consistency between the chip and the
 * card badge.
 */
const STATE_FILTER_OPTIONS: Array<{ value: RequestState; label: string }> = [
	{ value: RequestState.SUBMITTED, label: 'Submitted' },
	{ value: RequestState.IN_PROGRESS, label: 'In progress' },
	{ value: RequestState.PENDING_INFO, label: 'Waiting on submitter' },
	{ value: RequestState.APPROVED, label: 'Approved' },
	{ value: RequestState.COMPLETED, label: 'Completed' },
	{ value: RequestState.REJECTED, label: 'Rejected' },
	{ value: RequestState.CANCELLED, label: 'Cancelled' },
	{ value: RequestState.FAILED, label: 'Failed' },
]

const PAGE_SIZE = 20

/**
 * Caller's own requests (backend forces filters[submitter] for non-admins,
 * so we just hand it whatever filters the user picked here). Filters: state,
 * priority, type slug substring (client-side), date range. Pagination via
 * limit + offset, page count derived from `total`.
 */
export function MyRequestsPage() {
	const [page, setPage] = useState(0)
	const [filters, setFilters] = useState<ListRequestParams>({})
	const [typeFilter, setTypeFilter] = useState('')

	const { data, isLoading, error, isFetching } = useQuery({
		queryKey: requestCenterKeys.requests.list({ page, filters }),
		queryFn: () => requestCenterApi.listRequests({ ...filters, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
	})

	// Catalog fetch — used to map `requestTypeId` → human type name on each
	// row. Cached at the React Query layer; only one network hit per session.
	// `includeInactive: true` so soft-deleted types still render their name on
	// historical rows (rather than going blank).
	const { data: types } = useQuery({
		queryKey: requestCenterKeys.types.listWithInactiveCamel(),
		queryFn: () => requestCenterApi.listRequestTypes({ includeInactive: true }),
		staleTime: 5 * 60 * 1000,
	})
	const typeNameById = useMemo(() => {
		const map = new Map<string, string>()
		for (const t of types ?? []) map.set(t.id, t.name)
		return map
	}, [types])

	const items = data?.data ?? []
	const total = data?.total ?? 0
	const filtered =
		typeFilter.trim() === ''
			? items
			: items.filter((r) => r.requestNumber.toLowerCase().includes(typeFilter.toLowerCase()))
	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

	// Partition the visible page into "action needed" (PENDING_INFO) + the
	// rest. The split is purely visual — `filtered` order is preserved within
	// each group so the rest of the list stays date-sorted as before.
	const actionNeeded = filtered.filter((r) => r.currentState === RequestState.PENDING_INFO)
	const rest = filtered.filter((r) => r.currentState !== RequestState.PENDING_INFO)

	const actionNeededCount = actionNeeded.length
	const filterIsPendingInfo = filters.state === RequestState.PENDING_INFO

	return (
		<div className="space-y-6">
			<header className="flex flex-wrap items-start justify-between gap-3">
				<div className="space-y-1">
					<h1 className="text-2xl font-semibold tracking-tight">My Requests</h1>
					<p className="text-sm text-muted-foreground">
						{total} total
						{actionNeededCount > 0 && !filterIsPendingInfo ? (
							<>
								{' · '}
								<button
									type="button"
									className="font-medium text-amber-800 underline-offset-2 hover:underline"
									onClick={() => {
										setFilters({ ...filters, state: RequestState.PENDING_INFO })
										setPage(0)
									}}
									data-testid="action-needed-counter"
								>
									{actionNeededCount} awaiting your reply
								</button>
							</>
						) : null}
					</p>
				</div>
				<Button asChild>
					<Link to="/requests-center/catalog">Create a Request</Link>
				</Button>
			</header>

			<FiltersBar
				filters={filters}
				onFiltersChange={(next) => {
					setFilters(next)
					setPage(0)
				}}
				typeFilter={typeFilter}
				onTypeFilterChange={setTypeFilter}
			/>

			{isLoading ? (
				<ListSkeleton />
			) : error ? (
				<ErrorState message={error instanceof Error ? error.message : 'Failed to load requests'} />
			) : filtered.length === 0 ? (
				<EmptyState />
			) : (
				<>
					{actionNeeded.length > 0 ? (
						<section aria-label="Action needed" className="space-y-2" data-testid="action-needed-section">
							<h2 className="text-xs font-semibold uppercase tracking-wide text-amber-800">
								Action needed · {actionNeeded.length}
							</h2>
							<ul className="space-y-2">
								{actionNeeded.map((r) => (
									<ActionNeededRequestRow key={r.id} request={r} typeName={typeNameById.get(r.requestTypeId) ?? null} />
								))}
							</ul>
						</section>
					) : null}
					{rest.length > 0 ? (
						<ul className="space-y-2">
							{rest.map((r) => (
								<RequestRow key={r.id} request={r} typeName={typeNameById.get(r.requestTypeId) ?? null} />
							))}
						</ul>
					) : null}
				</>
			)}

			<Pagination page={page} totalPages={totalPages} disabled={isFetching} onChange={setPage} />
		</div>
	)
}

function FiltersBar({
	filters,
	onFiltersChange,
	typeFilter,
	onTypeFilterChange,
}: {
	filters: ListRequestParams
	onFiltersChange: (next: ListRequestParams) => void
	typeFilter: string
	onTypeFilterChange: (next: string) => void
}) {
	return (
		<div className="grid grid-cols-1 gap-3 md:grid-cols-4">
			<select
				className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
				value={filters.state ?? ''}
				onChange={(e) => onFiltersChange({ ...filters, state: e.target.value || undefined })}
				aria-label="Filter by state"
			>
				<option value="">All states</option>
				{STATE_FILTER_OPTIONS.map((opt) => (
					<option key={opt.value} value={opt.value}>
						{opt.label}
					</option>
				))}
			</select>
			<select
				className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
				value={filters.priority ?? ''}
				onChange={(e) => onFiltersChange({ ...filters, priority: e.target.value || undefined })}
				aria-label="Filter by priority"
			>
				<option value="">All priorities</option>
				<option value="normal">Normal</option>
				<option value="high">High</option>
				<option value="critical">Critical</option>
			</select>
			<Input
				type="search"
				value={typeFilter}
				onChange={(e) => onTypeFilterChange(e.target.value)}
				placeholder="Filter by SR number…"
				aria-label="Filter visible rows by SR number"
			/>
			<DateRange filters={filters} onFiltersChange={onFiltersChange} />
		</div>
	)
}

function DateRange({
	filters,
	onFiltersChange,
}: {
	filters: ListRequestParams
	onFiltersChange: (next: ListRequestParams) => void
}) {
	return (
		<div className="flex gap-2">
			<Input
				type="date"
				value={filters.from ?? ''}
				onChange={(e) => onFiltersChange({ ...filters, from: e.target.value || undefined })}
				aria-label="From date"
			/>
			<Input
				type="date"
				value={filters.to ?? ''}
				onChange={(e) => onFiltersChange({ ...filters, to: e.target.value || undefined })}
				aria-label="To date"
			/>
		</div>
	)
}

function RequestRow({ request, typeName }: { request: RequestDTO; typeName: string | null }) {
	return (
		<li>
			<Link to={`/requests-center/${request.id}`} className="block focus:outline-none">
				<Card className="flex items-center justify-between gap-3 p-4 transition-colors hover:border-primary/50 focus-visible:border-primary">
					<div className="min-w-0 space-y-1">
						<div className="flex flex-wrap items-center gap-2">
							<span className="font-mono text-sm font-medium">{request.requestNumber}</span>
							{typeName && <span className="text-sm font-medium text-foreground">{typeName}</span>}
							<Badge variant="outline">{request.priority}</Badge>
						</div>
						<RequestRowMeta request={request} />
					</div>
					<RequestStateBadge state={request.currentState} />
				</Card>
			</Link>
		</li>
	)
}

/**
 * Same body content as `<RequestRow>` but wrapped in an `<ActionNeededRowDecor>`
 * (amber border + faint background + "Reply now →" CTA). Used in the
 * "Action needed" section at the top of the list for requests in
 * `PENDING_INFO`. Renders an inline `<ReplyRequiredChip>` next to the SR
 * number so the row reads as urgent even when the row decor is clipped (e.g.
 * narrow viewports).
 */
function ActionNeededRequestRow({ request, typeName }: { request: RequestDTO; typeName: string | null }) {
	return (
		<li>
			<ActionNeededRowDecor to={`/requests-center/${request.id}`} dataTestId={`action-needed-row-${request.id}`}>
				<div className="space-y-1 p-4">
					<div className="flex flex-wrap items-center gap-2">
						<span className="font-mono text-sm font-medium">{request.requestNumber}</span>
						{typeName && <span className="text-sm font-medium text-foreground">{typeName}</span>}
						<Badge variant="outline">{request.priority}</Badge>
						<ReplyRequiredChip dataTestId={`reply-required-chip-${request.id}`} />
					</div>
					<RequestRowMeta request={request} />
				</div>
			</ActionNeededRowDecor>
		</li>
	)
}

function RequestRowMeta({ request }: { request: RequestDTO }) {
	// Backend computes `summary` at the controller boundary via
	// RequestSummaryRenderer — template-driven if the type has a
	// `summaryTemplate`, otherwise the auto-fallback. Empty string is a
	// valid result (e.g. an old request with no formData); we just hide
	// the line in that case rather than rendering "—".
	const summary = request.summary?.trim() ?? ''
	return (
		<>
			{summary ? <p className="truncate text-sm text-foreground/80">{summary}</p> : null}
			<p className="text-xs text-muted-foreground">Submitted {new Date(request.submittedAt).toLocaleString()}</p>
		</>
	)
}

function Pagination({
	page,
	totalPages,
	disabled,
	onChange,
}: {
	page: number
	totalPages: number
	disabled: boolean
	onChange: (next: number) => void
}) {
	return (
		<nav aria-label="Pagination" className="flex items-center justify-between">
			<p className="text-xs text-muted-foreground">
				Page {page + 1} of {totalPages}
			</p>
			<div className="flex gap-2">
				<Button variant="outline" disabled={disabled || page === 0} onClick={() => onChange(page - 1)}>
					Previous
				</Button>
				<Button variant="outline" disabled={disabled || page + 1 >= totalPages} onClick={() => onChange(page + 1)}>
					Next
				</Button>
			</div>
		</nav>
	)
}

function ListSkeleton() {
	return (
		<ul className="space-y-2">
			{[0, 1, 2, 3].map((i) => (
				<li key={i} className="h-16 animate-pulse rounded-lg border bg-muted/40" />
			))}
		</ul>
	)
}

function EmptyState() {
	return (
		<div className="rounded-md border border-dashed p-8 text-center">
			<p className="text-base font-medium">No requests yet</p>
			<p className="mt-1 text-sm text-muted-foreground">Submit your first one from the catalog.</p>
			<Button asChild className="mt-4">
				<Link to="/requests-center/catalog">Open the catalog</Link>
			</Button>
		</div>
	)
}

function ErrorState({ message }: { message: string }) {
	return (
		<div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
			{message}
		</div>
	)
}
